import React, { useState, useEffect } from 'react';
import { Download, Smartphone, X, Share, Plus, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

// Detect device type
const getDeviceInfo = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /chrome/i.test(ua) && !/edge/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
  
  return { isIOS, isAndroid, isSafari, isChrome, isStandalone };
};

// Main Install Button Component for Dashboard
export const InstallAppCard = ({ language = 'en' }) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { isIOS, isAndroid, isStandalone } = getDeviceInfo();

  const t = (en, sw) => language === 'sw' ? sw : en;

  useEffect(() => {
    // Check if already installed
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowInstructions(true);
    } else if (deferredPrompt) {
      // Android/Chrome - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions
      setShowInstructions(true);
    }
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  return (
    <>
      <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">
                  {t('Install Wekeza-Smart App', 'Pakua Programu ya Wekeza-Smart')}
                </h3>
                <p className="text-white/80 text-xs">
                  {t('Quick access from your home screen', 'Ufikiaji wa haraka kutoka skrini yako')}
                </p>
              </div>
            </div>
            <Button 
              onClick={handleInstallClick}
              className="bg-white text-emerald-600 hover:bg-gray-100 font-semibold"
              size="sm"
            >
              <Download className="h-4 w-4 mr-1" />
              {t('Install', 'Pakua')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions Dialog */}
      <InstallInstructionsDialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        language={language}
      />
    </>
  );
};

// Floating Install Button (for mobile)
export const FloatingInstallButton = ({ language = 'en' }) => {
  const [show, setShow] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const { isIOS, isStandalone } = getDeviceInfo();

  const t = (en, sw) => language === 'sw' ? sw : en;

  useEffect(() => {
    if (isStandalone) return;

    // Show button after 3 seconds
    const timer = setTimeout(() => setShow(true), 3000);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setShow(false));

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isStandalone]);

  const handleClick = async () => {
    if (isIOS) {
      setShowInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  if (!show || isStandalone || window.location.pathname.startsWith('/partner-desk')) return null;

  return (
    <>
      <div className="fixed bottom-20 left-4 z-50 md:hidden">
        <Button
          onClick={handleClick}
          className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg px-4 py-6 flex items-center gap-2 animate-bounce"
        >
          <Download className="h-5 w-5" />
          <span className="font-semibold">{t('Install App', 'Pakua')}</span>
        </Button>
        <button
          onClick={() => setShow(false)}
          className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <InstallInstructionsDialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        language={language}
      />
    </>
  );
};

// Instructions Dialog Component
const InstallInstructionsDialog = ({ open, onClose, language = 'en' }) => {
  const { isIOS, isAndroid, isSafari, isChrome } = getDeviceInfo();
  const t = (en, sw) => language === 'sw' ? sw : en;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-4 bg-gray-900 border-gray-700 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Smartphone className="h-5 w-5" />
            {t('Install Wekeza-Smart', 'Pakua Wekeza-Smart')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('Follow these simple steps to add the app to your home screen', 
               'Fuata hatua hizi rahisi kuongeza programu kwenye skrini yako')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-4">
              <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm font-medium mb-2">
                  {t('📱 For iPhone/iPad (Safari)', '📱 Kwa iPhone/iPad (Safari)')}
                </p>
              </div>
              
              <div className="space-y-3">
                <StepItem 
                  number={1}
                  icon={<Share className="h-5 w-5 text-blue-400" />}
                  title={t('Tap the Share button', 'Bofya kitufe cha Share')}
                  description={t('Look for the share icon at the bottom of Safari (square with arrow pointing up)', 
                                'Tafuta ikoni ya kushiriki chini ya Safari (mraba wenye mshale unaoelekea juu)')}
                />
                
                <StepItem 
                  number={2}
                  icon={<Plus className="h-5 w-5 text-emerald-400" />}
                  title={t('Tap "Add to Home Screen"', 'Bofya "Add to Home Screen"')}
                  description={t('Scroll down in the share menu and tap "Add to Home Screen"', 
                                'Sogeza chini kwenye menyu ya kushiriki na bofya "Add to Home Screen"')}
                />
                
                <StepItem 
                  number={3}
                  icon={<ChevronRight className="h-5 w-5 text-yellow-400" />}
                  title={t('Tap "Add"', 'Bofya "Add"')}
                  description={t('Confirm by tapping Add in the top right corner', 
                                'Thibitisha kwa kubofya Add kwenye kona ya juu kulia')}
                />
              </div>

              <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3 mt-4">
                <p className="text-yellow-300 text-xs">
                  💡 {t('Tip: Make sure you\'re using Safari browser for best experience', 
                        'Kidokezo: Hakikisha unatumia kivinjari cha Safari kwa uzoefu bora')}
                </p>
              </div>
            </div>
          ) : (
            // Android/Chrome Instructions
            <div className="space-y-4">
              <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-3">
                <p className="text-emerald-300 text-sm font-medium mb-2">
                  {t('📱 For Android (Chrome)', '📱 Kwa Android (Chrome)')}
                </p>
              </div>
              
              <div className="space-y-3">
                <StepItem 
                  number={1}
                  icon={<span className="text-lg">⋮</span>}
                  title={t('Tap the Menu button', 'Bofya kitufe cha Menyu')}
                  description={t('Look for three dots (⋮) in the top right corner of Chrome', 
                                'Tafuta nukta tatu (⋮) kwenye kona ya juu kulia ya Chrome')}
                />
                
                <StepItem 
                  number={2}
                  icon={<Plus className="h-5 w-5 text-emerald-400" />}
                  title={t('Tap "Add to Home screen"', 'Bofya "Add to Home screen"')}
                  description={t('Select "Add to Home screen" or "Install app" from the menu', 
                                'Chagua "Add to Home screen" au "Install app" kutoka menyu')}
                />
                
                <StepItem 
                  number={3}
                  icon={<Download className="h-5 w-5 text-blue-400" />}
                  title={t('Tap "Install"', 'Bofya "Install"')}
                  description={t('Confirm the installation to add Wekeza-Smart to your home screen', 
                                'Thibitisha usanikishaji kuongeza Wekeza-Smart kwenye skrini yako')}
                />
              </div>
            </div>
          )}

          {/* Benefits Section */}
          <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
            <h4 className="text-emerald-400 font-semibold mb-2 text-sm">
              {t('✨ Benefits of Installing', '✨ Faida za Kupakua')}
            </h4>
            <ul className="text-gray-300 text-xs space-y-1">
              <li>• {t('Quick access from home screen', 'Ufikiaji wa haraka kutoka skrini')}</li>
              <li>• {t('Works offline', 'Inafanya kazi bila mtandao')}</li>
              <li>• {t('Faster loading times', 'Muda wa kupakia haraka')}</li>
              <li>• {t('Full screen experience', 'Uzoefu wa skrini kamili')}</li>
              <li>• {t('Price alerts & notifications', 'Tahadhari za bei na arifa')}</li>
            </ul>
          </div>
        </div>

        <Button 
          onClick={onClose}
          className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600"
        >
          {t('Got it!', 'Nimeelewa!')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Step Item Component
const StepItem = ({ number, icon, title, description }) => (
  <div className="flex gap-3 bg-gray-800/50 rounded-lg p-3">
    <div className="flex-shrink-0">
      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
        {number}
      </div>
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-white font-medium text-sm">{title}</span>
      </div>
      <p className="text-gray-400 text-xs">{description}</p>
    </div>
  </div>
);

// Settings Page Install Section
export const InstallAppSection = ({ language = 'en' }) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { isIOS, isStandalone } = getDeviceInfo();

  const t = (en, sw) => language === 'sw' ? sw : en;

  useEffect(() => {
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isStandalone]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  return (
    <>
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-500/20 rounded-xl">
              <Smartphone className="h-8 w-8 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg mb-1">
                {t('Install Mobile App', 'Pakua Programu ya Simu')}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('Add Wekeza-Smart to your home screen for quick access, offline support, and a better experience.',
                   'Ongeza Wekeza-Smart kwenye skrini yako ya nyumbani kwa ufikiaji wa haraka, msaada wa nje ya mtandao, na uzoefu bora.')}
              </p>
              
              {isInstalled ? (
                <div className="flex items-center gap-2 text-emerald-400">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">{t('App Installed!', 'Programu Imepakiwa!')}</span>
                </div>
              ) : (
                <Button 
                  onClick={handleInstall}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isIOS 
                    ? t('How to Install on iPhone', 'Jinsi ya Kupakua kwenye iPhone')
                    : t('Install Now', 'Pakua Sasa')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <InstallInstructionsDialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        language={language}
      />
    </>
  );
};

export default InstallAppCard;
